import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';

import { CoiService } from '../../service/entity-services/coi.service';
import { DocumentViewerService, DocumentViewerResult } from '../../service/document-viewer.service';
import { Coi } from '../../objects/coi';

@Component({
  selector: 'app-coi-detail-inline',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDividerModule
  ],
  templateUrl: './coi-detail-inline.component.html',
  styleUrls: ['./coi-detail-inline.component.css']
})
export class CoiDetailInlineComponent implements OnChanges {
  @Input() coiNum: string = '';

  private coiService = inject(CoiService);
  private documentViewerService = inject(DocumentViewerService);
  /** Zoneless: a post-`await` assignment must notify Angular explicitly. */
  private cdrZoneless = inject(ChangeDetectorRef);
  private router = inject(Router);

  loading = false;
  coi: Coi | null = null;
  document: DocumentViewerResult | null = null;
  showDocument = false;

  // Display-friendly values
  partyProvidingCoiName = '';
  insuredPartyName = '';
  insuranceProviderName = '';
  primaryOwnerName = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['coiNum'] && this.coiNum) {
      this.loadCoi();
    }
  }

  private loadCoi() {
    this.loading = true;
    this.coi = null;
    this.document = null;

    this.coiService.getCoiByNumber(this.coiNum).subscribe(async coi => {
      if (coi) {
        this.coi = coi;
        this.processDisplayNames();
        if (coi.linkToCoi) {
          this.document = await this.documentViewerService.processDocument(coi.linkToCoi);
          this.cdrZoneless.markForCheck();   // zoneless: notify after await
        }
      }
      this.loading = false;
    });
  }

  private processDisplayNames() {
    if (!this.coi) return;

    this.partyProvidingCoiName = this.extractName(this.coi.partyProvidingCoi);
    this.insuredPartyName = this.extractName(this.coi.insuredParty, 'insuredPartyFriendlyName');
    this.primaryOwnerName = this.extractOwnerName(this.coi.primaryOwner);

    if (Array.isArray(this.coi.insuranceProvider) && this.coi.insuranceProvider.length > 0) {
      this.insuranceProviderName = this.coi.insuranceProvider
        .map((p: any) => this.extractName(p))
        .join(', ');
    } else {
      this.insuranceProviderName = this.extractName(this.coi.insuranceProvider);
    }
  }

  private extractName(value: any, friendlyKey?: string): string {
    if (!value) return 'N/A';
    if (typeof value === 'string') return value;
    if (friendlyKey && this.coi && (this.coi as any)[friendlyKey]) {
      return (this.coi as any)[friendlyKey];
    }
    if (typeof value === 'object') {
      return value.name || value.vendorName || value.legalEntityName || 'N/A';
    }
    return 'N/A';
  }

  private extractOwnerName(owner: any): string {
    if (!owner) return 'Not Assigned';
    if (typeof owner === 'string') return owner;
    if (owner.userData) {
      const ud = owner.userData;
      if (ud.family_name && ud.given_name) return `${ud.family_name}, ${ud.given_name}`;
      return ud.email || 'Not Assigned';
    }
    if (owner.fullName) return owner.fullName;
    if (owner.firstName || owner.lastName) {
      return [owner.lastName, owner.firstName].filter(Boolean).join(', ');
    }
    return owner.email || 'Not Assigned';
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  toggleDocument() {
    this.showDocument = !this.showDocument;
  }

  downloadDocument() {
    if (this.document) {
      this.documentViewerService.downloadFile(this.document.fetchUrl, this.document.fileName);
    }
  }

  viewFullRecord() {
    if (this.coi?.coiId) {
      this.router.navigate(['/securehome/coi', this.coi.coiId]);
    }
  }
}
